import { useEffect } from "react";
import { useBranch } from "@/contexts/BranchContext";

/**
 * Reusable Branch filter dropdown for list pages.
 *
 * Behaviour:
 *   • Unrestricted user (admin / `allowed_branch_ids` empty): full dropdown
 *     incl. "All Branches" option — picks any branch.
 *   • Restricted user with 2+ allowed branches: dropdown lists ONLY those
 *     branches, plus an "All My Branches" option that maps to "" (so the
 *     existing list-filter logic `!filterBranch || row.branch_id === ...`
 *     keeps working unchanged).
 *   • Restricted user with EXACTLY 1 allowed branch: renders a static badge
 *     (no dropdown) showing the branch name AND auto-locks the parent's
 *     `filterBranch` state to that id via `onChange`. They cannot switch.
 *
 * Usage:
 *   const [filterBranch, setFilterBranch] = useState("");
 *   <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="si-list-filter-branch" />
 *   ...then filter: rows.filter(r => !filterBranch || r.branch_id === filterBranch)
 */
export function BranchFilter({ value, onChange, testid = "list-filter-branch", className = "" }) {
  const { branches, isRestricted } = useBranch();
  const list = branches || [];

  // Restricted user with a single allowed branch — lock the value and show
  // a non-interactive badge. We use useEffect so we don't write state during
  // render (avoids "setState in render" warnings).
  const lockedToSingle = isRestricted && list.length === 1;
  useEffect(() => {
    if (lockedToSingle && value !== list[0].id) {
      onChange(list[0].id);
    }
  }, [lockedToSingle, list, value, onChange]);

  if (lockedToSingle) {
    return (
      <div
        data-testid={`${testid}-locked`}
        title="Your account is restricted to this branch. Contact admin for access to other branches."
        className={`inline-flex items-center gap-1.5 border border-slate-300 px-2.5 py-1.5 text-xs bg-slate-50 normal-case min-w-[140px] cursor-not-allowed select-none ${className}`}
      >
        <svg width="11" height="11" viewBox="0 0 16 16" fill="none" className="text-slate-500" xmlns="http://www.w3.org/2000/svg">
          <path d="M4 7V5a4 4 0 1 1 8 0v2h1a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1zm2 0h4V5a2 2 0 1 0-4 0v2z" fill="currentColor"/>
        </svg>
        <span className="font-semibold text-slate-700">{list[0].name || list[0].branch_name || list[0].id}</span>
      </div>
    );
  }

  return (
    <select
      data-testid={testid}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className={`border border-slate-300 px-2 py-1.5 text-xs bg-white focus:border-[#F97316] focus:outline-none normal-case min-w-[140px] ${className}`}
    >
      <option value="">{isRestricted ? "All My Branches" : "All Branches"}</option>
      {list.map((b) => (
        <option key={b.id} value={b.id}>{b.name || b.branch_name || b.id}</option>
      ))}
    </select>
  );
}

export default BranchFilter;
